import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, useMotionValue, animate, useTransform } from 'framer-motion';
import { RefreshCw } from 'lucide-react';

/**
 * Pull-to-refresh wrapper for mobile lists.
 *
 * Listens for native touch events on the document body and, when the user
 * starts a downward drag from the very top of the page (scrollTop === 0),
 * progressively reveals a refresh indicator. When the user has dragged past
 * the threshold and releases, ``onRefresh`` is invoked; the indicator
 * remains visible until the returned promise resolves.
 *
 * Only activates on coarse pointers (touch devices). Desktop is a no-op.
 *
 * Props:
 *   onRefresh: () => Promise<void>
 *   children:  the page content
 *   threshold: pixels of pull required to trigger refresh (default 70)
 *   maxPull:   maximum visual offset (default 120)
 */
const DEFAULTS = { threshold: 70, maxPull: 120 };

function useIsTouch() {
  const [touch, setTouch] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    setTouch(window.matchMedia('(pointer: coarse)').matches);
  }, []);
  return touch;
}

export function PullToRefresh({
  onRefresh,
  children,
  threshold = DEFAULTS.threshold,
  maxPull = DEFAULTS.maxPull,
  testId = 'pull-to-refresh',
}) {
  const isTouch = useIsTouch();
  const y = useMotionValue(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(null);
  const dragging = useRef(false);

  // Indicator visuals progress from 0 → 1 as the user drags.
  const indicatorOpacity = useTransform(y, [0, threshold * 0.4, threshold], [0, 0.6, 1]);
  const indicatorRotate = useTransform(y, [0, maxPull], [0, 360]);

  const reset = useCallback(() => {
    animate(y, 0, { type: 'spring', stiffness: 320, damping: 28 });
    startY.current = null;
    dragging.current = false;
  }, [y]);

  const onTouchStart = useCallback((e) => {
    if (refreshing) return;
    if (window.scrollY > 0) return;  // only trigger from very top
    startY.current = e.touches[0].clientY;
  }, [refreshing]);

  const crossedThreshold = useRef(false);
  const onTouchMove = useCallback((e) => {
    if (refreshing || startY.current == null) return;
    const delta = e.touches[0].clientY - startY.current;
    if (delta <= 0) {
      // user dragged up — abort
      if (dragging.current) reset();
      crossedThreshold.current = false;
      return;
    }
    if (window.scrollY > 0) {
      // user scrolled in another direction; cancel
      reset();
      crossedThreshold.current = false;
      return;
    }
    dragging.current = true;
    // rubber-band the drag so it feels resistant past maxPull
    const dampened = delta < maxPull ? delta : maxPull + (delta - maxPull) * 0.3;
    const next = Math.min(dampened, maxPull + 30);
    y.set(next);
    // Haptic blip the moment user crosses the trigger threshold.
    if (!crossedThreshold.current && next >= threshold) {
      crossedThreshold.current = true;
      if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(10);
    } else if (crossedThreshold.current && next < threshold) {
      crossedThreshold.current = false;
    }
  }, [refreshing, maxPull, threshold, y, reset]);

  const onTouchEnd = useCallback(async () => {
    if (refreshing || !dragging.current) { startY.current = null; return; }
    const passed = y.get() >= threshold;
    if (!passed) {
      reset();
      return;
    }
    setRefreshing(true);
    if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate([8, 40, 8]);
    animate(y, threshold, { duration: 0.12 });
    try {
      await Promise.resolve(onRefresh?.());
    } finally {
      setRefreshing(false);
      reset();
    }
  }, [refreshing, threshold, y, onRefresh, reset]);

  useEffect(() => {
    if (!isTouch) return;
    const opts = { passive: true };
    document.addEventListener('touchstart', onTouchStart, opts);
    document.addEventListener('touchmove', onTouchMove, opts);
    document.addEventListener('touchend', onTouchEnd, opts);
    document.addEventListener('touchcancel', onTouchEnd, opts);
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [isTouch, onTouchStart, onTouchMove, onTouchEnd]);

  if (!isTouch) {
    return <>{children}</>;
  }

  return (
    <>
      <motion.div
        aria-hidden
        style={{ opacity: indicatorOpacity }}
        className="fixed top-2 left-1/2 -translate-x-1/2 z-40 pointer-events-none"
        data-testid={`${testId}-indicator`}
      >
        <div className="bg-white shadow-lg border border-slate-200 rounded-full w-10 h-10 flex items-center justify-center">
          <motion.div style={{ rotate: refreshing ? undefined : indicatorRotate }}>
            <RefreshCw className={`w-4 h-4 text-[#0047AB] ${refreshing ? 'animate-spin' : ''}`} />
          </motion.div>
        </div>
      </motion.div>
      <motion.div style={{ y }} data-testid={testId}>
        {children}
      </motion.div>
    </>
  );
}
